import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useRouter } from "@tanstack/react-router";
import { useBaseTableHooks } from "./hooks";
import type { BaseTableProps } from "./types";

const TableBodyData = <T extends { id: number }>({
  rows: data,
  columns,
  to,
  params,
  noDataMessage,
  onRowClick,
}: BaseTableProps<T> & { noDataMessage: string }) => {
  const router = useRouter();
  const handleRowClick = (row: T) => {
    if (to) {
      router.navigate({ to: to, params: params ? params(row) : undefined });
    }
    if (onRowClick) {
      onRowClick(row);
    }
  };
  if (data.length === 0)
    return (
      <TableRow
        sx={{
          width: "100%",
          height: "160px",
          bgColor: grey[200],
          border: "none",
        }}
      >
        <TableCell color={grey[500]} colSpan={12} align="center">
          {noDataMessage}
        </TableCell>
      </TableRow>
    );
  return (
    <>
      {data
        .filter((row) => row.id)
        .map((row) => {
          return (
            <TableRow
              key={`${row.id}`}
              hover
              sx={{ cursor: to || onRowClick ? "pointer" : "inherit" }}
              onClick={() => {
                handleRowClick(row);
              }}
            >
              {columns.map((column, index) => (
                <TableCell
                  width={column.width}
                  key={`${String(column.key)}:${index}`}
                  sx={{
                    whiteSpace: "pre-wrap",
                    cursor: column.disabledLink ? "default" : "inherit",
                    ...column.tbodySx,
                  }}
                  align={column.align}
                  onClick={(e) => {
                    column.disabledLink && e.stopPropagation();
                  }}
                >
                  {column.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
    </>
  );
};

export function BaseTable<T extends { id: number }>({
  rows,
  columns,
  to,
  params,
  noDataMessage,
  onRowClick,
}: BaseTableProps<T>) {
  const { sortColumn, sortDirection, handleSort, filteredAndSortedData } =
    useBaseTableHooks<T>({ rows, columns, noDataMessage });

  return (
    <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.50" }}>
            {columns.map((column, index) => (
              <TableCell
                key={`${String(column.key)}-${index}`}
                align={column.align}
                sx={{ whiteSpace: "nowrap" }}
              >
                {column.sortable ? (
                  <TableSortLabel
                    active={sortColumn === column.key}
                    direction={
                      sortColumn === column.key ? sortDirection : "asc"
                    }
                    onClick={() => handleSort(column.key)}
                    sx={{
                      fontWeight: sortColumn === column.key ? 700 : 400,
                      "& .MuiTableSortLabel-icon": {
                        fontWeight: 700,
                        color: `${grey[900]} !important`,
                      },
                    }}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableBodyData
            rows={filteredAndSortedData}
            columns={columns}
            to={to}
            params={params}
            noDataMessage={noDataMessage}
            onRowClick={onRowClick}
          />
        </TableBody>
      </Table>
    </TableContainer>
  );
}
